import {
   defaultUserLocaleByPhpLangid,
   fromPhpLangId,
   type PhpLangid,
   toPhpLangId,
} from './php-langid';
import type { LocaleCode } from './types';
import {
   type UserLocaleCode,
   countryByCode,
   defaultUserLocaleByRouteLocale,
   isUserLocaleCode,
   parseUserLocale,
   routeLocaleByUserLocale,
   userLocalesByRouteLocale,
} from './user-locale';

export interface UserLocaleSignals {
   /** Raw `user-locale` cookie value (explicit choice). */
   cookieValue: string | null | undefined;
   /** PHP `view-locale` cookie / stored route locale preference. */
   phpViewLocale: string | null | undefined;
   /** PHP `view-langid` cookie / stored language preference. */
   phpViewLangid: string | null | undefined;
   /** PHP langid stored on the account (logged-in users). */
   accountLangid: string | null | undefined;
   /** Geo-IP-derived user locale. */
   geoUserLocale: UserLocaleCode | null;
}

/** Precedence: explicit cookie → PHP region + language → account language → geo → default → en-US. */
function resolveUserLocale(
   signals: UserLocaleSignals,
   match: (userLocale: UserLocaleCode | null) => UserLocaleCode | null,
   defaultUserLocale: UserLocaleCode | undefined
): UserLocaleCode {
   return (
      match(parseRawCookieValue(signals.cookieValue)) ??
      match(
         resolvePhpPreference(signals.phpViewLocale, signals.phpViewLangid, signals.accountLangid)
      ) ??
      match(resolveAccountPreference(signals.accountLangid, signals.geoUserLocale)) ??
      match(signals.geoUserLocale) ??
      defaultUserLocale ??
      'en-US'
   );
}

const identityLocale = (code: LocaleCode): LocaleCode => code;

/**
 * Resolve the user locale for a Next.js route locale (e.g. `en-us`).
 * `demoteRouteLocale` maps unpublished route locales to the published locale
 * users are actually served.
 */
export function resolveUserLocaleForRouteLocale(
   currentRouteLocale: LocaleCode,
   signals: UserLocaleSignals,
   demoteRouteLocale: (code: LocaleCode) => LocaleCode = identityLocale
): UserLocaleCode {
   return resolveUserLocale(
      signals,
      userLocale => matchingRouteLocale(userLocale, currentRouteLocale, demoteRouteLocale),
      defaultUserLocaleByRouteLocale[currentRouteLocale]
   );
}

/**
 * Resolve the user locale from stored preferences alone, for surfaces whose
 * URL carries no locale.
 */
export function resolveUserLocaleFromPreference(
   signals: UserLocaleSignals,
   defaultUserLocale?: UserLocaleCode
): UserLocaleCode {
   return resolveUserLocale(signals, userLocale => userLocale, defaultUserLocale);
}

/** Resolve the user locale for a Carpenter PHP language id (e.g. `en`, `fr`). */
export function resolveUserLocaleForPhpLangId(
   currentPhpLangId: PhpLangid,
   signals: UserLocaleSignals
): UserLocaleCode {
   return resolveUserLocale(
      signals,
      userLocale => matchingPhpLangId(userLocale, currentPhpLangId),
      defaultUserLocaleByPhpLangid[currentPhpLangId]
   );
}

export function parseRawCookieValue(value: string | null | undefined): UserLocaleCode | null {
   if (value == null) return null;
   return isUserLocaleCode(value) ? value : null;
}

function matchingRouteLocale(
   userLocale: UserLocaleCode | null,
   currentRouteLocale: LocaleCode,
   demoteRouteLocale: (code: LocaleCode) => LocaleCode
): UserLocaleCode | null {
   if (!userLocale) return null;
   return demoteRouteLocale(routeLocaleByUserLocale[userLocale]) === currentRouteLocale
      ? userLocale
      : null;
}

function matchingPhpLangId(
   userLocale: UserLocaleCode | null,
   currentPhpLangid: string
): UserLocaleCode | null {
   if (!userLocale) return null;
   return toPhpLangId(userLocale.slice(0, 2)) === currentPhpLangid ? userLocale : null;
}

/**
 * A locale of the geo country in the account language when one exists, else
 * the language's home locale. Cookies win over the account so a picker
 * selection applies even while the account save is in flight (or failed).
 */
function resolveAccountPreference(
   accountLangid: string | null | undefined,
   geoUserLocale: UserLocaleCode | null
): UserLocaleCode | null {
   if (!accountLangid) return null;

   const language = fromPhpLangId(accountLangid);
   if (geoUserLocale) {
      const { country } = parseUserLocale(geoUserLocale);
      const countryMatch = countryByCode[country].locales.find(
         locale => parseUserLocale(locale).language === language
      );
      if (countryMatch) return countryMatch;
   }

   return defaultUserLocaleByPhpLangid[accountLangid as PhpLangid] ?? null;
}

/**
 * The view-locale cookie is a region choice; the language comes from the
 * account when one is logged in, mirroring PHP's
 * UserLanguagePreference::getLangid, which ignores the anonymous view-langid
 * cookie once logged in. A language with no configured locale in the region
 * falls through so the account and geo tiers can keep the language instead
 * of losing it to the region's default.
 */
function resolvePhpPreference(
   viewLocale: string | null | undefined,
   viewLangid: string | null | undefined,
   accountLangid: string | null | undefined
): UserLocaleCode | null {
   if (viewLocale == null) return null;

   // Own-key check: the cookie is client-controlled and inherited keys like
   // "toString" would pass a truthiness check with a function value.
   if (!Object.prototype.hasOwnProperty.call(defaultUserLocaleByRouteLocale, viewLocale)) {
      return null;
   }
   const regionDefault = defaultUserLocaleByRouteLocale[viewLocale as LocaleCode];
   if (!regionDefault) return null;

   const langid = accountLangid || viewLangid;
   if (!langid) return regionDefault;

   // The region's locales are the route's own plus the rest of its country:
   // nl-NL lives on the en-eu rollup route, while fr-CA is the Canadian
   // store's French but lives on route fr-ca.
   const language = fromPhpLangId(langid);
   const candidates = [
      ...(userLocalesByRouteLocale[viewLocale as LocaleCode] ?? []),
      ...countryByCode[parseUserLocale(regionDefault).country].locales,
   ];
   const match = candidates.find(locale => parseUserLocale(locale).language === language);
   if (match) return match;

   // Only an account language may leave the tier: the account tier below
   // re-places it. An anonymous language has no tier of its own, and falling
   // through would let a geo guess override the stored region.
   return accountLangid ? null : regionDefault;
}
